import Swiper from 'swiper';
import { Navigation, Thumbs } from 'swiper/modules';
import { Modal } from 'bootstrap-italia';
import 'swiper/swiper-bundle.css';
import './gallery-immobile.scss';
import './gallery-immobile-modal.scss';

const SELECTORS = {
  SECTION: '.js-gallery-immobile',
  OPEN_BTN: '.js-gallery-immobile-open',
  YT_PREVIEW: '.js-gallery-immobile-yt-preview',
  MODAL: '.js-gallery-immobile-modal',
  TAB_BTN: '.js-gallery-immobile-tab',
  PANEL: '.js-gallery-immobile-panel',
  MAIN_SWIPER: '.js-gallery-immobile-main',
  THUMBS_SWIPER: '.js-gallery-immobile-thumbs',
};

const CLASS = {
  ACTIVE: 'is-active',
};

/**
 * version    1.0
 * class      GalleryImmobile
 * classdesc  Component that manages the property gallery modal with tabs (Video, Foto, Planimetria),
 *            a main Swiper viewer and a synchronized thumbnail Swiper per each panel.
 *            Handles video stop on slide change and modal close.
 */
export class GalleryImmobile {
  constructor() {
    this._el = document.querySelector(SELECTORS.SECTION);
    if (!this._el) return;

    this._modal = document.querySelector(SELECTORS.MODAL);
    if (!this._modal) return;

    this._tabBtns = Array.from(this._modal.querySelectorAll(SELECTORS.TAB_BTN));
    this._panels = Array.from(this._modal.querySelectorAll(SELECTORS.PANEL));
    this._swipers = {};
    this._swipersReady = false;
    this._bsModal = null;

    this._initYouTubePreview();
    this._initNavVisibility();
    this._initPlanimetriaZoom();
    this._bindEvents();
  }

  _initYouTubePreview() {
    [this._el, this._modal].forEach((root) => {
      root.querySelectorAll(SELECTORS.YT_PREVIEW).forEach((preview) => {
        const activate = () => {
          const { youtubeId } = preview.dataset;
          if (!youtubeId) return;

          const iframe = document.createElement('iframe');
          iframe.className = 'gallery-immobile__yt-iframe';
          iframe.src = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&enablejsapi=1`;
          const previewImg = preview.querySelector('img');
          iframe.title = (previewImg && previewImg.alt) || 'Video';
          iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute('loading', 'lazy');

          // Sostituisce il div preview con l'iframe
          preview.replaceWith(iframe);

          // Aggiorna il swiper della modal se il preview era dentro una slide
          const panelEl = iframe.closest('.js-gallery-immobile-panel');
          if (panelEl && this._swipersReady) {
            const type = panelEl.dataset.panel;
            if (this._swipers[type]) this._swipers[type].main.update();
          }
        };

        preview.addEventListener('click', activate);
        preview.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate();
          }
        });
      });
    });
  }

  _bindEvents() {
    // Pulsanti di apertura nella sezione pagina (sidebar)
    this._el.querySelectorAll(SELECTORS.OPEN_BTN).forEach((btn) => {
      btn.addEventListener('click', () => {
        this._openModal(btn.dataset.tab);
      });
    });

    // Tab switching nella modale
    this._tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        this._switchTab(btn.dataset.tab);
      });
    });

    // Keyboard navigation per i tab (arrow keys)
    this._tabBtns.forEach((btn, index) => {
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          const next = this._tabBtns[index + 1] || this._tabBtns[0];
          next.focus();
          this._switchTab(next.dataset.tab);
        }
        if (e.key === 'ArrowLeft') {
          const prev = this._tabBtns[index - 1] || this._tabBtns[this._tabBtns.length - 1];
          prev.focus();
          this._switchTab(prev.dataset.tab);
        }
      });
    });

    // Init swipers al primo show della modale (solo quando il DOM è visibile)
    this._modal.addEventListener('shown.bs.modal', () => {
      if (!this._swipersReady) {
        this._initSwipers();
        this._swipersReady = true;
      }
      // Forza update del swiper attivo per ricalcolare le dimensioni
      const activeType = this._getActiveTab();
      if (activeType && this._swipers[activeType]) {
        this._swipers[activeType].main.update();
        if (this._swipers[activeType].thumbs) {
          this._swipers[activeType].thumbs.update();
        }
      }
    });

    // Stop tutti i video alla chiusura della modale
    this._modal.addEventListener('hidden.bs.modal', () => {
      this._stopAllVideos();
    });
  }

  _openModal(tab) {
    this._switchTab(tab || 'video');

    if (!this._bsModal) {
      this._bsModal = new Modal(this._modal);
    }
    this._bsModal.show();
  }

  _switchTab(tab) {
    // Ferma eventuali video in riproduzione prima di cambiare tab
    this._stopAllVideos();

    // Aggiorna stato dei pulsanti tab
    this._tabBtns.forEach((btn) => {
      const isActive = btn.dataset.tab === tab;
      btn.classList.toggle(CLASS.ACTIVE, isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });

    // Aggiorna visibilità panels
    this._panels.forEach((panel) => {
      const isActive = panel.dataset.panel === tab;
      panel.classList.toggle(CLASS.ACTIVE, isActive);
      // Gestione attributo hidden per accessibilità
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    // Aggiorna swiper del tab attivo (se già inizializzato)
    if (this._swipersReady && this._swipers[tab]) {
      this._swipers[tab].main.update();
      if (this._swipers[tab].thumbs) {
        this._swipers[tab].thumbs.update();
      }
    }
  }

  _initNavVisibility() {
    const isMobile = !window.matchMedia('(min-width: 992px)').matches;
    this._panels.forEach((panel) => {
      const mainEl = panel.querySelector(SELECTORS.MAIN_SWIPER);
      if (!mainEl) return;

      const slideCount = mainEl.querySelectorAll('.swiper-slide').length;
      const prevEl = panel.querySelector('.gallery-immobile-modal__nav--prev');
      const nextEl = panel.querySelector('.gallery-immobile-modal__nav--next');

      if (slideCount <= 1 || isMobile) {
        if (prevEl) prevEl.setAttribute('hidden', '');
        if (nextEl) nextEl.setAttribute('hidden', '');
      }
    });
  }

  _initSwipers() {
    this._panels.forEach((panel) => {
      const type = panel.dataset.panel;
      const mainEl = panel.querySelector(SELECTORS.MAIN_SWIPER);
      const thumbsEl = panel.querySelector(SELECTORS.THUMBS_SWIPER);
      const prevEl = panel.querySelector('.gallery-immobile-modal__nav--prev');
      const nextEl = panel.querySelector('.gallery-immobile-modal__nav--next');

      if (!mainEl) return;

      const slideCount = mainEl.querySelectorAll('.swiper-slide').length;
      const hasMultipleSlides = slideCount > 1;

      // Swiper miniature
      let thumbsSwiper = null;
      if (thumbsEl) {
        thumbsSwiper = new Swiper(thumbsEl, {
          spaceBetween: 8,
          slidesPerView: 'auto',
          watchSlidesProgress: true,
          freeMode: true,
          breakpoints: {
            // >= 992px
            992: {
              spaceBetween: 20,
            },
          },
        });
      }

      // Swiper principale
      const mainSwiper = new Swiper(mainEl, {
        modules: [Navigation, Thumbs],
        spaceBetween: 0,
        ...(hasMultipleSlides && prevEl && nextEl && {
          navigation: {
            prevEl,
            nextEl,
          },
        }),
        ...(thumbsSwiper && {
          thumbs: { swiper: thumbsSwiper },
        }),
        on: {
          // Ferma i video quando si cambia slide
          slideChange: () => {
            this._stopAllVideos();
          },
        },
      });

      this._swipers[type] = { main: mainSwiper, thumbs: thumbsSwiper };
    });
  }

  _getActiveTab() {
    const activeBtn = this._tabBtns.find((btn) => btn.classList.contains(CLASS.ACTIVE));
    return activeBtn ? activeBtn.dataset.tab : null;
  }

  _initPlanimetriaZoom() {
    const planimetriaPanel = this._modal.querySelector('[data-panel="planimetria"]');
    if (!planimetriaPanel) return;

    planimetriaPanel.addEventListener('click', (e) => {
      const figure = e.target.closest('.gallery-immobile-modal__slide-figure--planimetria');
      if (!figure) return;

      const img = figure.querySelector('img');
      if (!img) return;

      const url = img.src || img.dataset.src;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    });
  }

  _stopAllVideos() {
    [this._el, this._modal].forEach((root) => {
      root.querySelectorAll('iframe[src*="youtube"]').forEach((iframe) => {
        try {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
            '*',
          );
        } catch (e) {
          // Ignora errori cross-origin
        }
      });
    });
  }
}
